import { useState, useCallback } from "react";
import { validateAllFields } from "../utils/brandFormValidation";

export interface BrandFormData {
  brandName: string;
  businessType: "SERVICE" | "PRODUCT" | "BOTH" | "";
  industry: string;
  website: string;
  phoneNumber: string;
  serviceArea: string[];
  servicesOffered: string[];
  productsOffered: string[];
  tagline: string;
  /** The mark for light backgrounds. Pre-SGL-344 this was simply "the logo". */
  logoUrl: string;
  logoMediaId: string;
  /** The mark for dark backgrounds (SGL-344). Optional, like its light twin. */
  logoUrlDark: string;
  logoMediaIdDark: string;
  colors: Array<{
    value: string;
    type: "primary" | "secondary" | "accent" | "other";
  }>;
  brandVoice: string;
  targetAudience: string;
  primaryCTA: string[];
  avoidWords: string[];
  mustIncludeWords: string[];
}

export interface UseBrandFormReturn {
  formData: BrandFormData;
  errors: Record<string, string>;
  isSaving: boolean;
  hasChanges: boolean;
  updateField: (field: keyof BrandFormData, value: string) => void;
  validateForm: () => boolean;
  resetForm: () => void;
  setFormData: (data: Partial<BrandFormData>) => void;
}

const initialFormData: BrandFormData = {
  brandName: "",
  businessType: "",
  industry: "",
  website: "",
  phoneNumber: "",
  serviceArea: [],
  servicesOffered: [],
  productsOffered: [],
  tagline: "",
  logoUrl: "",
  logoMediaId: "",
  logoUrlDark: "",
  logoMediaIdDark: "",
  // Starts empty (SGL-224). This used to seed #2f4941 — Sagelyn's own accent green
  // — into every new customer's palette. Presented as their brand with nothing
  // marking it as a placeholder, and the field being filled meant nothing prompted
  // them to fix it, so customers generated all their content in our colour.
  // An empty palette produces an obvious gap; a pre-filled one produces
  // confidently wrong output that looks deliberate.
  colors: [],
  brandVoice: "",
  targetAudience: "",
  primaryCTA: [],
  avoidWords: [],
  mustIncludeWords: [],
};

export const useBrandForm = (
  initialData?: Partial<BrandFormData>
): UseBrandFormReturn => {
  const [formData, setFormDataState] = useState<BrandFormData>({
    ...initialFormData,
    ...initialData,
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  /**
   * Update a single form field
   */
  const updateField = useCallback(
    (field: keyof BrandFormData, value: string) => {
      setFormDataState((prev) => ({
        ...prev,
        [field]: value,
      }));
      
      // Clear error for this field when user starts typing
      if (errors[field]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
      
      setHasChanges(true);
    },
    [errors]
  );

  /**
   * Validate entire form
   */
  const validateForm = useCallback((): boolean => {
    const validationErrors = validateAllFields(formData);
    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  }, [formData]);

  /**
   * Reset form to initial state
   */
  const resetForm = useCallback(() => {
    setFormDataState({
      ...initialFormData,
      ...initialData,
    });
    setErrors({});
    setHasChanges(false);
  }, [initialData]);

  /**
   * Set form data programmatically
   */
  const setFormData = useCallback((data: Partial<BrandFormData>) => {
    setFormDataState((prev) => ({
      ...prev,
      ...data,
    }));
  }, []);

  return {
    formData,
    errors,
    isSaving,
    hasChanges,
    updateField,
    validateForm,
    resetForm,
    setFormData,
  };
};
